import {
  Avatar,
  GrammaticalGender,
  SupportedLocale,
  useFinishAiInterviewMutation,
  useJoinConferenceMutation,
} from 'generated/graphql';
import { Room } from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';
import { getIsBeforeUnloadActive } from '../ConferencePage';
import { analyticsClient } from '@spotted-zebra-uk/ui-components';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';

const LIVEKIT_SERVER_URL = import.meta.env.VITE_LIVEKIT_URL as
  | string
  | undefined;

const LIVEKIT_TOKEN_LOCAL_STORAGE_KEY = 'livekit_token';

type UseConferenceOptions = {
  conferenceId: string | undefined;
  aiInterviewCandidateId: string;
  onError?: (error: Error) => void;
};

type JoinConferenceParams = {
  avatar: Avatar;
  allowAgentInterruptions?: boolean;
  locale: SupportedLocale;
  grammaticalGender?: GrammaticalGender;
};

type ConferenceState = {
  room: Room | null;
  isJoining: boolean;
  isConnected: boolean;
  error: Error | null;
  joinConference: (params: JoinConferenceParams) => Promise<void>;
  leaveConference: () => Promise<void>;
};

export function useConference({
  conferenceId,
  aiInterviewCandidateId,
  onError,
}: UseConferenceOptions): ConferenceState {
  const [room] = useState(
    () =>
      new Room({
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
        adaptiveStream: true,
        dynacast: true,
        // Enable audio level observation for all participants
        audioOutput: {
          deviceId: 'default',
        },
        // Disable automatic disconnect on page leave - we handle this ourselves
        // to allow users to cancel navigation and continue the interview
        disconnectOnPageLeave: false,
      })
  );

  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (room.state === 'connected') {
      setIsConnected(true);
      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_CONNECTED,
      });
    }

    if (room.state === 'disconnected') {
      setIsConnected(false);
      analyticsClient.trackEvent({
        name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_DISCONNECTED,
      });
    }
  }, [room.state]);

  useEffect(() => {
    if (!error || (!error?.name && !error?.message)) return;

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_FAILED,
      properties: {
        error: error.name,
        errorDescr: error.message,
      },
    });
  }, [error]);

  // GraphQL mutation to get LiveKit token
  const [joinConferenceMutation, { loading: isJoining }] =
    useJoinConferenceMutation({
      onCompleted: async data => {
        try {
          const livekitToken = data.joinConference.token;

          localStorage.setItem(LIVEKIT_TOKEN_LOCAL_STORAGE_KEY, livekitToken);
          if (!LIVEKIT_SERVER_URL) {
            throw new Error('Missing livekit URL configuration variable!');
          }

          await room.connect(LIVEKIT_SERVER_URL, livekitToken);

          setIsConnected(true);
          setError(null);
        } catch (err) {
          const error =
            err instanceof Error
              ? err
              : new Error('Failed to connect to conference');
          setError(error);
          onError?.(error);
        }
      },
      onError: err => {
        const error = new Error(`Failed to join conference: ${err.message}`);
        setError(error);
        onError?.(error);
      },
    });

  const [finishAiInterviewMutation] = useFinishAiInterviewMutation({
    onCompleted: () => {
      setIsConnected(false);
      setError(null);
    },
    onError: err => {
      const error = new Error(`Failed to finish AI interview: ${err.message}`);
      setError(error);
      onError?.(error);
    },
  });

  const joinConference = useCallback(
    async ({
      avatar,
      allowAgentInterruptions,
      locale,
      grammaticalGender,
    }: JoinConferenceParams) => {
      if (!conferenceId) {
        return;
      }

      // Prevent joining if already connected or connecting
      if (room.state === 'connected' || room.state === 'connecting') {
        return;
      }

      try {
        setError(null);

        // Request permissions - we need to stop this stream immediately
        // because LiveKit will create its own streams when we enable camera/mic
        const permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        // Stop the permission stream tracks immediately to release the camera/mic
        // LiveKit will request its own streams when we call setMicrophoneEnabled/setCameraEnabled
        permissionStream.getTracks().forEach(track => track.stop());

        room.localParticipant.setMicrophoneEnabled(true);
        room.localParticipant.setCameraEnabled(true);

        await joinConferenceMutation({
          variables: {
            id: conferenceId,
            input: {
              avatar,
              ...(allowAgentInterruptions !== undefined && {
                allowAgentInterruptions,
              }),
              locale,
              grammaticalGender,
            },
          },
        });
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to get media permissions');
        setError(error);
        onError?.(error);
        console.error('❌ Failed to get media permissions:', err);
      }
    },
    [conferenceId, room, joinConferenceMutation, onError]
  );

  const leaveConference = useCallback(async () => {
    // Stop all local tracks to release camera/microphone
    room.localParticipant.trackPublications.forEach(publication => {
      if (publication.track) {
        // Stop the LiveKit track
        publication.track.stop();
        // Also stop the underlying MediaStreamTrack if available
        if (publication.track.mediaStreamTrack) {
          publication.track.mediaStreamTrack.stop();
        }
      }
    });

    room.disconnect();

    await finishAiInterviewMutation({
      variables: {
        id: aiInterviewCandidateId,
      },
    });
    localStorage.removeItem(LIVEKIT_TOKEN_LOCAL_STORAGE_KEY);
    setIsConnected(false);
  }, [room, aiInterviewCandidateId, finishAiInterviewMutation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't run cleanup if we're in the middle of a beforeunload event
      // The user might cancel the navigation and stay on the page
      if (getIsBeforeUnloadActive()) {
        return;
      }

      // Stop all local tracks before disconnecting to ensure camera/mic are released
      room.localParticipant.trackPublications.forEach(publication => {
        if (publication.track) {
          publication.track.stop();
          // Also stop the underlying MediaStreamTrack if available
          if (publication.track.mediaStreamTrack) {
            publication.track.mediaStreamTrack.stop();
          }
        }
      });

      if (room.state !== 'disconnected') {
        room.disconnect();
      }
    };
  }, [room]);

  return {
    room,
    isJoining,
    isConnected,
    error,
    joinConference,
    leaveConference,
  };
}
